/**
 * types.ts — Core type definitions for the Canvas 2D office renderer.
 *
 * Ported from pixel-agents reference implementation.
 * Zero Phaser imports. Zero external dependencies.
 */

// ── Re-exported constants needed by types ────────────────────
export const TILE_SIZE = 16;
export const DEFAULT_COLS = 20;
export const DEFAULT_ROWS = 11;
export const MAX_COLS = 64;
export const MAX_ROWS = 64;
export const MATRIX_EFFECT_DURATION_SEC = 0.3;

// ── Tile Types ────────────────────────────────────────────────
export const TileType = {
  WALL: 0,
  FLOOR_1: 1,
  FLOOR_2: 2,
  FLOOR_3: 3,
  FLOOR_4: 4,
  FLOOR_5: 5,
  FLOOR_6: 6,
  FLOOR_7: 7,
  FLOOR_8: 8,
  FLOOR_9: 9,
  VOID: 255,
} as const;
export type TileType = (typeof TileType)[keyof typeof TileType];

/** Per-tile color settings for floor pattern colorization */
export interface FloorColor {
  /** Hue: 0-360 in colorize mode, -180 to +180 in adjust mode */
  h: number;
  /** Saturation: 0-100 in colorize mode, -100 to +100 in adjust mode */
  s: number;
  /** Brightness -100 to 100 */
  b: number;
  /** Contrast -100 to 100 */
  c: number;
  /** When true, use Photoshop-style Colorize (grayscale → fixed HSL). Default: adjust mode. */
  colorize?: boolean;
}

// ── Character State & Direction ───────────────────────────────
export const CharacterState = {
  IDLE: 'idle',
  WALK: 'walk',
  TYPE: 'type',
} as const;
export type CharacterState = (typeof CharacterState)[keyof typeof CharacterState];

export const Direction = {
  DOWN: 0,
  LEFT: 1,
  RIGHT: 2,
  UP: 3,
} as const;
export type Direction = (typeof Direction)[keyof typeof Direction];

// ── Sprite Data ───────────────────────────────────────────────
/** 2D array of hex color strings: '' = transparent, '#RRGGBB' = opaque, '#RRGGBBAA' = semi-transparent. [row][col] */
export type SpriteData = string[][];

// ── Seat ──────────────────────────────────────────────────────
export interface Seat {
  /** Chair furniture uid */
  uid: string;
  /** Tile col where agent sits */
  seatCol: number;
  /** Tile row where agent sits */
  seatRow: number;
  /** Direction character faces when sitting (toward adjacent desk) */
  facingDir: Direction;
  assigned: boolean;
}

// ── Furniture ─────────────────────────────────────────────────
export interface FurnitureInstance {
  sprite: SpriteData;
  /** Pixel x (top-left) */
  x: number;
  /** Pixel y (top-left) */
  y: number;
  /** Y value used for depth sorting (typically bottom edge) */
  zY: number;
  /** Render-time horizontal flip flag (for mirrored side variants) */
  mirrored?: boolean;
}

// ── Tool Activity ─────────────────────────────────────────────
export interface ToolActivity {
  toolId: string;
  status: string;
  done: boolean;
  permissionWait?: boolean;
}

// ── Edit Tool ─────────────────────────────────────────────────
export const EditTool = {
  TILE_PAINT: 'tile_paint',
  WALL_PAINT: 'wall_paint',
  FURNITURE_PLACE: 'furniture_place',
  FURNITURE_PICK: 'furniture_pick',
  SELECT: 'select',
  EYEDROPPER: 'eyedropper',
  ERASE: 'erase',
} as const;
export type EditTool = (typeof EditTool)[keyof typeof EditTool];

// ── Furniture Catalog ─────────────────────────────────────────
export interface FurnitureCatalogEntry {
  type: string; // asset ID from furniture manifest
  label: string;
  footprintW: number;
  footprintH: number;
  sprite: SpriteData;
  isDesk: boolean;
  category?: string;
  /** Orientation from rotation group: 'front' | 'back' | 'left' | 'right' */
  orientation?: string;
  /** Whether this item can be placed on top of desk/table surfaces */
  canPlaceOnSurfaces?: boolean;
  /** Number of tile rows from the top of the footprint that are "background" (allow placement, still block walking). Default 0. */
  backgroundTiles?: number;
  /** Whether this item can be placed on wall tiles */
  canPlaceOnWalls?: boolean;
  /** Whether this is a side-oriented asset that produces a mirrored "left" variant */
  mirrorSide?: boolean;
}

// ── Layout ────────────────────────────────────────────────────
export interface PlacedFurniture {
  uid: string;
  type: string; // asset ID from furniture manifest
  col: number;
  row: number;
  /** Optional color override for furniture */
  color?: FloorColor;
}

/** Department room zone within a multi-room office layout (Phase 6+) */
export interface RoomDefinition {
  /** Unique room identifier, e.g., "uiux", "dev", "executive", "advisor", "support" */
  id: string;
  /** Display label, e.g., "UI/UX Studio" */
  name: string;
  /** Matches role-mapping key in roomHelpers.ts */
  department: string;
  bounds: {
    /** Left tile col (inclusive) */
    x: number;
    /** Top tile row (inclusive) */
    y: number;
    /** Width in tiles */
    w: number;
    /** Height in tiles */
    h: number;
  };
  /** Canonical color for this room's floor tiles */
  floorColor: FloorColor;
  /** e.g., "creative_studio" | "hacker_den" | "corner_office" | "lounge" | "support_nook" */
  furniturePreset: string;
}

export interface OfficeLayout {
  version: 1;
  cols: number;
  rows: number;
  tiles: TileType[];
  furniture: PlacedFurniture[];
  /** Per-tile color settings, parallel to tiles array. null = wall/no color */
  tileColors?: Array<FloorColor | null>;
  /** Bumped when the bundled default layout changes; forces a reset on existing installs */
  layoutRevision?: number;
  /** Department room zones — optional for backward compat with default-layout-1.json (Phase 6+) */
  rooms?: RoomDefinition[];
}

// ── PM Character State ────────────────────────────────────────
export type PMCharacterState = 'IDLE' | 'WORKING' | 'ON_FIRE' | 'IN_REVIEW' | 'BLOCKED' | 'AFK' | 'CELEBRATING';

// ── Flat Asset Entry ──────────────────────────────────────────
/** A fully resolved asset entry from a furniture manifest */
export interface FlatAssetEntry {
  id: string;           // e.g., "PC_FRONT_OFF", "DESK_FRONT", "BIN"
  file: string;         // e.g., "PC_FRONT_OFF.png"
  dirName: string;      // e.g., "PC", "DESK", "BIN" (parent directory)
  width: number;        // pixel width
  height: number;       // pixel height
  footprintW: number;   // tile width
  footprintH: number;   // tile height
  category: string;     // e.g., "electronics", "desks", "misc"
  isDesk: boolean;      // true if category === "desks"
  label: string;        // human-readable label
  orientation?: string; // "front" | "back" | "side"
  state?: string;       // "on" | "off"
  frame?: number;       // animation frame index
  groupId?: string;     // parent group ID for rotation/state
  rotationScheme?: string; // "2-way" | "3-way-mirror" | "4-way"
  animationGroup?: string; // animation group ID
  canPlaceOnSurfaces?: boolean;
  canPlaceOnWalls?: boolean;
  backgroundTiles?: number;
  mirrorSide?: boolean;
}

// ── Character ─────────────────────────────────────────────────
export interface Character {
  id: number;
  state: CharacterState;
  dir: Direction;
  /** Pixel position */
  x: number;
  y: number;
  /** Current tile column */
  tileCol: number;
  /** Current tile row */
  tileRow: number;
  /** Remaining path steps (tile coords) */
  path: Array<{ col: number; row: number }>;
  /** 0-1 lerp between current tile and next tile */
  moveProgress: number;
  /** Current tool name for typing vs reading animation, or null */
  currentTool: string | null;
  /** Palette index (0-5) */
  palette: number;
  /** Hue shift in degrees (0 = no shift, ≥45 for repeated palettes) */
  hueShift: number;
  /** Animation frame index */
  frame: number;
  /** Time accumulator for animation */
  frameTimer: number;
  /** Timer for idle wander decisions */
  wanderTimer: number;
  /** Number of wander moves completed in current roaming cycle */
  wanderCount: number;
  /** Max wander moves before returning to seat for rest */
  wanderLimit: number;
  /** Whether the agent is actively working */
  isActive: boolean;
  /** Assigned seat uid, or null if no seat */
  seatId: string | null;
  /** Active speech bubble type, or null if none showing */
  bubbleType: 'permission' | 'waiting' | null;
  /** Countdown timer for bubble (waiting: 2→0, permission: unused) */
  bubbleTimer: number;
  /** Timer to stay seated while inactive after seat reassignment (counts down to 0) */
  seatTimer: number;
  /** Whether this character represents a sub-agent (spawned by Task tool) */
  isSubagent: boolean;
  /** Parent agent ID if this is a sub-agent, null otherwise */
  parentAgentId: number | null;
  /** Active matrix spawn/despawn effect, or null */
  matrixEffect: 'spawn' | 'despawn' | null;
  /** Timer counting up from 0 to MATRIX_EFFECT_DURATION_SEC */
  matrixEffectTimer: number;
  /** Per-column random seeds (16 values) for staggered rain timing */
  matrixEffectSeeds: number[];
  /** Workspace folder name (only set for multi-root workspaces) */
  folderName?: string;
  /** PM semantic state — drives Phase 3 UI color coding and badges */
  pmState: PMCharacterState;
  /** Department string used to locate this character's waiting room (Phase 12) */
  department?: string;
  /** Desk seat UID stored before a waiting-room move. Restored by returnToDesk(). null = no desk seat recorded. */
  homeSeatId?: string | null;
  /** Display name for MemberDetailPanel (Phase 3) */
  name?: string;
  /** Custom per-member sprite set generated from CharacterConfig (overrides palette-based sprites) */
  customSprites?: import('./spriteData').CharacterSprites;
}
